// src/features/schedule/components/UpNextCard.tsx
import React from 'react';
import { ScheduleItem } from '../../../types/scheduleItem';
import { Task } from '../../../types/task';
import { format } from 'date-fns';
import { MapPin } from 'lucide-react';
import { DayPhase } from '../../../hooks/useDayPhase';

interface UpNextCardProps {
  nextItem: ScheduleItem | null;
  secondNextItem?: ScheduleItem | null;
  isLoading?: boolean;
  dayPhase?: DayPhase;
  topOpenTasks?: Task[];
}

const UpNextCard: React.FC<UpNextCardProps> = ({
  nextItem,
  secondNextItem,
  isLoading,
  dayPhase = 'active',
  topOpenTasks = [],
}) => {
  // 載入中骨架屏
  if (isLoading && !nextItem) {
    return (
      <div className="relative overflow-hidden p-5 md:p-6 rounded-xl md:rounded-1.5xl bg-white border border-gray-200 animate-pulse">
        <div className="h-3 w-16 bg-gray-200 rounded mb-4" />
        <div className="space-y-3">
          <div className="h-5 w-40 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  // Evening：隱藏整個卡片，空間讓給 DayCloseSection
  if (!nextItem && dayPhase === 'evening') {
    return null;
  }

  if (!nextItem) {
    // Morning：壓縮高度，簡單一行
    if (dayPhase === 'morning') {
      return (
        <div className="relative overflow-hidden px-5 py-4 rounded-xl md:rounded-1.5xl bg-white border border-gray-200">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            NEXT UP
          </div>
          <p className="text-sm text-gray-400">Your day is open</p>
        </div>
      );
    }

    // Active Day / fallback：task 清單已移入 CurrentScheduleItemCard，此處隱藏
    return null;
  }

  // 有下一個排程項目
  const formattedTimeRange = nextItem.endTime
    ? `${format(nextItem.startTime, 'HH:mm')}–${format(nextItem.endTime, 'HH:mm')}`
    : format(nextItem.startTime, 'HH:mm');

  return (
    <div className="relative overflow-hidden p-5 md:p-6 rounded-xl md:rounded-1.5xl bg-white border border-gray-200">
      <div className="relative z-10">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
          NEXT UP
        </div>

        <div className="flex justify-between items-baseline mb-2">
          <h3 className="text-gray-700 font-semibold text-xl flex-1 mr-3">
            {nextItem.title}
          </h3>
          <div className="text-gray-500 text-base flex-shrink-0">
            {formattedTimeRange}
          </div>
        </div>

        {nextItem.location && (
          <div className="flex items-center text-sm text-gray-500 mt-2">
            <MapPin className="w-4 h-4 mr-1" />
            <span>{nextItem.location}</span>
          </div>
        )}

        {secondNextItem && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-start gap-2 text-sm">
              <span className="text-gray-400 font-medium flex-shrink-0">Then:</span>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-600 font-medium truncate flex-1 mr-3">
                    {secondNextItem.title}
                  </span>
                  <span className="text-gray-500 text-xs flex-shrink-0">
                    {secondNextItem.endTime
                      ? `${format(secondNextItem.startTime, 'HH:mm')}–${format(secondNextItem.endTime, 'HH:mm')}`
                      : format(secondNextItem.startTime, 'HH:mm')
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpNextCard;
